import { global_variables } from '../../../../environments/global_variables';

export type ValvePumpHeader = 'idleValue' | 'targetValue' | 't1' | 't2' | 't3';

export const VALVE_PUMP_HEADERS: ReadonlyArray<ValvePumpHeader> = ['idleValue', 'targetValue', 't1', 't2', 't3'];

/** Upper bound for every timing cell (t1, t2, t3), in seconds. */
export const VALVE_PUMP_TIME_MAX = 14400;

/**
 * One row of the Measurement Run value/pump table. Rows are described rather than
 * addressed by index, so new rows can be inserted without shifting the others.
 */
export interface ValvePumpRow {
   /** Step JSON key, also the form-control prefix (`${key}_${header}`). */
   key: string;
   label: string;
   /** Idle/target value cells: an on/off select, or a number within [min, max]. */
   kind: 'onoff' | 'numeric';
   min?: number;
   max?: number;
   unit?: string;
   /** t2 is N/A (disabled) for on/off rows. */
   t2: 'na' | 'numeric';
   /** Saved inside the step's Special_Config rather than at step level. */
   specialConfig?: boolean;
}

export const OMNI_2100_VALVE_PUMP_ROWS: ReadonlyArray<Readonly<ValvePumpRow>> = [
   { key: 'Valve1', label: 'Valve1', kind: 'onoff', t2: 'na' },
   { key: 'Valve2', label: 'Valve2', kind: 'onoff', t2: 'na' },
   { key: 'Valve3', label: 'Valve3', kind: 'onoff', t2: 'na' },
   { key: 'Sampling_Pump', label: 'Sampling Pump', kind: 'onoff', t2: 'na' },
   { key: 'Separation_Pump_Power', label: 'Separation Pump Power', kind: 'onoff', t2: 'na' },
   {
      key: 'Separation_Pump_Amplitude',
      label: 'Separation Pump Amplitude',
      kind: 'numeric',
      min: 0,
      max: 1.3,
      unit: 'V',
      t2: 'numeric',
   },
   {
      key: 'Separation_Pump_Frequency',
      label: 'Separation Pump Frequency',
      kind: 'numeric',
      min: 0,
      max: 1000,
      unit: 'Hz',
      t2: 'numeric',
   },
];

/** Omni-30xx adds Valve4 and the PID lamp, both on/off like the valves, after Valve3. */
const OMNI_30XX_EXTRA_ROWS: ReadonlyArray<Readonly<ValvePumpRow>> = [
   { key: 'Valve4', label: 'Valve4', kind: 'onoff', t2: 'na', specialConfig: true },
   { key: 'PID_Lamp', label: 'PID lamp', kind: 'onoff', t2: 'na', specialConfig: true },
];

/** Omni-30xx drives the separation pump by stroke delay instead of amplitude and frequency (R-1.5). */
const OMNI_30XX_SEPARATION_PUMP_ROWS: ReadonlyArray<Readonly<ValvePumpRow>> = [
   {
      key: 'Separation_Pump_Stroke_Delay',
      label: 'Separation Pump Stroke Delay',
      kind: 'numeric',
      min: 0,
      max: 65536,
      t2: 'numeric',
      specialConfig: true,
   },
];

const OMNI_2100_ONLY_KEYS = ['Separation_Pump_Amplitude', 'Separation_Pump_Frequency'];

const AFTER_VALVE3 = OMNI_2100_VALVE_PUMP_ROWS.findIndex(row => row.key === 'Valve3') + 1;

export const OMNI_30XX_VALVE_PUMP_ROWS: ReadonlyArray<Readonly<ValvePumpRow>> = [
   ...OMNI_2100_VALVE_PUMP_ROWS.slice(0, AFTER_VALVE3),
   ...OMNI_30XX_EXTRA_ROWS,
   ...OMNI_2100_VALVE_PUMP_ROWS.slice(AFTER_VALVE3).filter(row => !OMNI_2100_ONLY_KEYS.includes(row.key)),
   ...OMNI_30XX_SEPARATION_PUMP_ROWS,
];

export const isValueHeader = (header: string): boolean => header === 'idleValue' || header === 'targetValue';

export const isOnOffCell = (row: ValvePumpRow, header: string): boolean => row.kind === 'onoff' && isValueHeader(header);

export const isNotApplicableCell = (row: ValvePumpRow, header: string): boolean => row.t2 === 'na' && header === 't2';

/** Value used when a step has no saved value for a cell. */
export const valvePumpCellDefault = (row: ValvePumpRow, header: string): string | number => {
   if (isOnOffCell(row, header)) {
      return global_variables['onOffStatus'][1];
   }
   if (isNotApplicableCell(row, header)) {
      return 'N/A';
   }
   return 0;
};

export const SPECIAL_CONFIG_KEY = 'Special_Config';

/** The object a row's values are written to within a step: the step, or its Special_Config (created on demand). */
export const valvePumpTarget = (step: any, row: ValvePumpRow): any => {
   if (!row.specialConfig) {
      return step;
   }
   step[SPECIAL_CONFIG_KEY] = step[SPECIAL_CONFIG_KEY] || {};
   return step[SPECIAL_CONFIG_KEY];
};

/**
 * A row's saved values within a step. Special_Config rows fall back to the step level,
 * where Omni-30xx configs saved before Special_Config was introduced kept them.
 */
export const savedValvePumpValues = (step: any, row: ValvePumpRow): any => {
   if (!step) {
      return undefined;
   }
   if (row.specialConfig) {
      return step[SPECIAL_CONFIG_KEY]?.[row.key] ?? step[row.key];
   }
   return step[row.key];
};
